import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { X } from "lucide-react";

const ANIM_MS = 320;
const navBottom = () => {
    const h = document.querySelector('[data-testid="desktop-header"]');
    const r = h && h.offsetParent !== null ? h.getBoundingClientRect() : null;
    return r ? Math.max(0, r.bottom) : 0;
};

// Account / Owner Tools: a sheet that slides out from under the nav bar over the page. No scroll lock, so nothing jumps.
export function NavDropPanel({ open, onClose, kicker, title, tabs = [], active, onTab, wide = false, testid, children }) {
    const [mounted, setMounted] = useState(open);
    const [shown, setShown] = useState(false);
    const [top, setTop] = useState(0);
    useEffect(() => {
        if (open) { setMounted(true); return undefined; }
        setShown(false);
        const t = setTimeout(() => setMounted(false), ANIM_MS);
        return () => clearTimeout(t);
    }, [open]);
    useLayoutEffect(() => {
        if (!mounted || !open) return undefined;
        const place = () => setTop(navBottom());
        place();
        const raf = requestAnimationFrame(() => setShown(true));
        window.addEventListener("resize", place);
        window.addEventListener("scroll", place, { passive: true });
        return () => { cancelAnimationFrame(raf); window.removeEventListener("resize", place); window.removeEventListener("scroll", place); };
    }, [mounted, open]);
    const sheet = useRef(null);
    useEffect(() => {
        if (!open) return undefined;
        const k = (e) => { if (e.key === "Escape") onClose(); };
        // Click above (nav) or below the panel closes it (its own nav buttons toggle themselves; pop-overs opened from inside count as inside).
        const down = (e) => {
            // Left click only (middle-click autoscroll / right-click never close). Clicks on the sheet itself — sides included — stay open.
            if (e.button !== 0) return;
            const t = e.target;
            if (!(t instanceof Element) || sheet.current?.contains(t)) return;
            if (t.closest('[data-testid^="access-acc_"], [data-testid^="access-own_"], [data-radix-popper-content-wrapper], [role="dialog"], [role="menu"], [role="listbox"], [data-sonner-toaster]')) return;
            onClose();
        };
        window.addEventListener("keydown", k);
        document.addEventListener("pointerdown", down, true);
        return () => { window.removeEventListener("keydown", k); document.removeEventListener("pointerdown", down, true); };
    }, [open, onClose]);
    if (!mounted) return null;
    const ease = `${ANIM_MS}ms cubic-bezier(0.22,1,0.36,1)`;
    return (
        <div className="fixed inset-x-0 bottom-0 z-40" style={{ top }} data-testid={testid}>
            <button type="button" aria-label="Close" onClick={onClose} data-testid={`${testid}-backdrop`}
                className="absolute inset-0 bg-black/55 backdrop-blur-[2px] cursor-default" style={{ opacity: shown ? 1 : 0, transition: `opacity ${ease}` }} />
            <div ref={sheet} className="absolute inset-x-0 top-0 max-h-full overflow-y-auto border-b border-white/10 shadow-[0_24px_60px_rgba(0,0,0,0.6)]"
                style={{ background: "radial-gradient(1200px 500px at 50% -20%, rgba(157,76,221,0.18), transparent), #06070C", clipPath: shown ? "inset(0 0 0 0)" : "inset(0 0 100% 0)", transform: shown ? "translateY(0)" : "translateY(-16px)", transition: `clip-path ${ease}, transform ${ease}` }}>
                <div className={`relative mx-auto px-4 sm:px-6 py-7 ${wide ? "max-w-7xl" : "max-w-3xl"}`}>
                    <button type="button" onClick={onClose} data-testid={`${testid}-close`} title="Close"
                        className="absolute right-4 top-5 h-9 w-9 inline-flex items-center justify-center rounded-xl border border-white/10 bg-black/30 text-white/60 hover:text-white hover:border-white/30 transition-colors"><X className="h-4 w-4" /></button>
                    <div className="text-center mb-5">
                        <div className="text-[10px] font-black uppercase tracking-[0.2em] text-[#9D4CDD]" data-testid={`${testid}-kicker`}>{kicker}</div>
                        <h1 className="font-heading text-2xl font-black uppercase tracking-tight mt-1 text-white" data-testid={`${testid}-title`}>{title}</h1>
                    </div>
                    {tabs.length > 1 && (
                        <div className="flex flex-wrap justify-center gap-2 mb-6" data-testid={`${testid}-tabs`}>
                            {tabs.map(([k, l]) => (
                                <button key={k} type="button" onClick={() => onTab(k)} data-testid={`${testid}-tab-${k}`}
                                    className={`px-3.5 py-2 rounded-xl border text-xs font-black uppercase tracking-wider transition-colors ${active === k ? "bg-[#9D4CDD] border-[#9D4CDD] text-white" : "bg-black/30 border-white/10 text-white/70 hover:text-white hover:border-white/30"}`}>{l}</button>
                            ))}
                        </div>
                    )}
                    <div key={active} className="fade-up">{children}</div>
                </div>
            </div>
        </div>
    );
}
